import { CombinableBet, mapCombinableBetslipBets, mapCombinableLiveBets, mapCombinablePrematchBets } from './types';
import { MarketParticipantCheck } from '../turboLive/dto';
import { Participant } from '../../types/generated/nikeone';
import { currentPicks$ } from '../betslip/command';

export function hasCombinableLiveMarket(markets, allowMarketCombinations) {
    return markets?.find(market => {
        return (
            allowMarketCombinations &&
            (market.get('marketCombinable') || (market.get('marketGroupId') && market.get('marketGroupId') !== 0))
        );
    });
}

export function getCombinableLiveMarkets(markets, allowMarketCombinations) {
    return markets?.filter(market => {
        return (
            allowMarketCombinations && (market.marketCombinable || (market.marketGroupId && market.marketGroupId !== 0))
        );
    });
}

export function hasCombinablePrematchBet(bets) {
    return bets?.find(bet => {
        const combinationRules = bet.get('combinationRules');
        return (
            combinationRules &&
            combinationRules.get('tournamentAllowMarketCombinations') &&
            (combinationRules.get('marketCombinable') ||
                (combinationRules.get('marketGroupId') && combinationRules.get('marketGroupId') !== 0))
        );
    });
}

export function getPrematchBetsByCombinableMarkets(bets) {
    return bets?.filter(bet => {
        const combinationRules = bet.combinationRules;
        return (
            combinationRules.tournamentAllowMarketCombinations &&
            (combinationRules.marketCombinable ||
                (combinationRules.marketGroupId && combinationRules.marketGroupId !== 0))
        );
    });
}

export function getLiveBetsByCombinableMarkets(bets, markets) {
    const combinableMarketIds = markets?.map(market => market.id);
    return bets?.filter(bet => combinableMarketIds.includes(bet.marketId));
}

export function getCombinablePrematchBets(bets, matchId) {
    const combinableBets = getPrematchBetsByCombinableMarkets(bets);
    const mappedCombinableBets = mapCombinablePrematchBets(combinableBets);
    const { validCombinableBets, validMappedCombinableBets } = validateCombinableBets(
        combinableBets,
        mappedCombinableBets
    );
    return (
        validCombinableBets &&
        validMappedCombinableBets &&
        validateBetsWithBetslip(validCombinableBets, validMappedCombinableBets, matchId)
    );
}

export function getCombinableLiveBets(bets, markets, allowMarketCombinations, matchId) {
    const combinableMarkets = getCombinableLiveMarkets(markets, allowMarketCombinations);
    const combinableBets = getLiveBetsByCombinableMarkets(bets, combinableMarkets);
    const mappedCombinableBets = mapCombinableLiveBets(combinableBets, combinableMarkets, allowMarketCombinations);
    const { validCombinableBets, validMappedCombinableBets } = validateCombinableBets(
        combinableBets,
        mappedCombinableBets
    );

    return (
        validCombinableBets &&
        validMappedCombinableBets &&
        validateBetsWithBetslip(validCombinableBets, validMappedCombinableBets, matchId)
    );
}

const validateCombinableBets = (combinableBets, mappedCombinableBets) => {
    const { validMappedCombinableBetIds, validMappedCombinableBets } = findAllCombinableBets(mappedCombinableBets);
    if (validMappedCombinableBetIds.size < 1) return {};
    const validCombinableBets = combinableBets.filter(bet => validMappedCombinableBetIds.has(bet?.id || bet?.betId));

    return { validCombinableBets, validMappedCombinableBets };
};

export const isNewBetCombinable = (newBet, oldBets, markets, allowMarketCombinations, matchId) => {
    const combinableMarkets = getCombinableLiveMarkets(markets, allowMarketCombinations);
    const combinableMarketIds = combinableMarkets?.map(combinableMarket => combinableMarket.id);
    const isCombinableMarket = combinableMarketIds && combinableMarketIds.includes(newBet.marketId);

    if (isCombinableMarket) {
        const mappedCombinableNewBet = mapCombinableLiveBets(
            Array.of(newBet),
            combinableMarkets,
            allowMarketCombinations
        );
        const combinableBets = getLiveBetsByCombinableMarkets(oldBets, combinableMarkets);
        const mappedCombinableBets = mapCombinableLiveBets(combinableBets, combinableMarkets, allowMarketCombinations);
        const firstCombinableWith = findFirstCombinableWith(mappedCombinableNewBet, mappedCombinableBets);
        if (firstCombinableWith)
            return validateBetsWithBetslip(Array.of(newBet), mappedCombinableNewBet, matchId)?.length > 0;
        else return false;
    }
    return false;
};

export function validateBetExclusion(newBet: CombinableBet, oldBets: CombinableBet[]): Array<CombinableBet> {
    const exclusionConflictBets = findConflictingBetsFromSameMatch(newBet, oldBets);
    if (exclusionConflictBets) {
        return exclusionConflictBets;
    }
    return [];
}

const validateBetsWithBetslip = (combinableBets, mappedCombinableBets, matchId) => {
    const betslipBets = currentPicks$
        .getValue()
        .valueSeq()
        .toArray()
        .filter((bet: Map<string, any>) => bet.get('betEventId') === matchId);
    if (!betslipBets || betslipBets.length === 0) {
        return combinableBets;
    }
    const combinableBetslipBets = mapCombinableBetslipBets(betslipBets);
    const exclusionBets = combinableBetslipBets.map((bet: CombinableBet) =>
        validateBetExclusion(bet, mappedCombinableBets)
    );
    const exclusionBetIds = new Set(exclusionBets.flat().map(bet => bet.betId));

    return combinableBets.filter(bet => !exclusionBetIds.has(bet?.id || bet?.betId));
};

// find all bets that are combinable with at least one other bet
const findAllCombinableBets = (mappedCombinableBets: CombinableBet[]) => {
    const validMappedCombinableBetIds = new Set<string>();
    const validMappedCombinableBets = [];
    mappedCombinableBets?.forEach(processedBet => {
        // skip if bet is already combinable
        if (!validMappedCombinableBetIds.has(processedBet.betId)) {
            const firstCombinableWith = findFirstCombinableWith(processedBet, mappedCombinableBets);
            // if bet betA is combinable with betB, both are combinable
            if (firstCombinableWith) {
                validMappedCombinableBetIds.add(processedBet.betId).add(firstCombinableWith.betId);
                validMappedCombinableBets.push(processedBet, firstCombinableWith);
            }
        }
    });

    return { validMappedCombinableBetIds, validMappedCombinableBets };
};

// find first bet that can be combined with currently processed bet
const findFirstCombinableWith = (processedBet, mappedCombinableBets) => {
    return mappedCombinableBets.find(
        oldBet =>
            processedBet.betId !== oldBet.betId &&
            areBetsFromSameMatch(processedBet, oldBet) &&
            areBetsFromSameMatchAllowed(processedBet, oldBet)
    );
};

function findConflictingBetsFromSameMatch(newBet: CombinableBet, oldBets: CombinableBet[]) {
    return oldBets
        .filter(bet => bet.betId !== newBet.betId)
        .filter(bet => areBetsFromSameMatch(bet, newBet) && !areBetsFromSameMatchAllowed(bet, newBet));
}

function areBetsFromSameMatch(bet1: CombinableBet, bet2: CombinableBet) {
    return bet1.betEventId && bet2.betEventId && bet1.betEventId === bet2.betEventId;
}

function areBetsFromSameMatchAllowed(bet1: CombinableBet, bet2: CombinableBet) {
    if (!checkUniqueParticipants(bet1, bet2)) {
        return false;
    }

    if (betsFromSameMarket(bet1, bet2)) {
        return bet1.marketTicketCombinable && bet2.marketTicketCombinable;
    }

    if (betsFromSameMarketGroup(bet1, bet2)) {
        return false;
    }

    if (allowMarketConbinationMask(bet1, bet2)) {
        return (bet1.marketGroupCombinationMask & bet2.marketGroupCombinationMask) !== 0;
    }

    return false;
}

function checkUniqueParticipants(bet1: CombinableBet, bet2: CombinableBet) {
    if (bet1.participants === null || bet2.participants === null) {
        return true;
    }
    const participantCombinations = bet1.participants.flatMap(p1 => bet2.participants.map(p2 => [p1, p2]));
    return !participantCombinations.some(
        tuple =>
            isParticipantConflict(bet1.marketUniqueParticipantCheck, tuple[0], tuple[1]) ||
            isParticipantConflict(bet2.marketUniqueParticipantCheck, tuple[1], tuple[0])
    );
}

function isParticipantConflict(check: MarketParticipantCheck, participant1: Participant, participant2: Participant) {
    if (check === null) {
        return false;
    }

    return checkParticipantConflict(check, participant1, participant2);
}

function checkParticipantConflict(check: MarketParticipantCheck, participant1: Participant, participant2: Participant) {
    switch (check) {
        case MarketParticipantCheck.direct:
            return isParticipantsDirectConflict(participant1, participant2);
        case MarketParticipantCheck.team_once:
            return isParticipantsTeamOnceConflict(participant1, participant2);
        case MarketParticipantCheck.with_team:
            return isParticipantsWithTeamConflict(participant1, participant2);
        default:
            return false;
    }
}

function isParticipantsDirectConflict(participant1: Participant, participant2: Participant) {
    return participant1.participantId === participant2.participantId;
}

function isParticipantsWithTeamConflict(participant1: Participant, participant2: Participant) {
    if (participant1.participantType === 'team') {
        return (
            participant1.participantId !== participant2.participantId &&
            participant1.participantId !== participant2.parentId
        );
    } else {
        return (
            participant1.participantId !== participant2.participantId && participant1.parentId !== participant2.parentId
        );
    }
}

function isParticipantsTeamOnceConflict(participant1: Participant, participant2: Participant) {
    return getTeamId(participant1) === getTeamId(participant2);
}

function getTeamId(participant: Participant) {
    if (participant.participantType === 'team') {
        return participant.participantId;
    }

    return participant.parentId;
}

function betsFromSameMarket(bet1: CombinableBet, bet2: CombinableBet) {
    return bet1.marketId !== null && bet2.marketId !== null && bet1.marketId === bet2.marketId;
}

function betsFromSameMarketGroup(bet1: CombinableBet, bet2: CombinableBet) {
    return bet1.marketGroupId && bet2.marketGroupId && bet1.marketGroupId === bet2.marketGroupId;
}

function allowMarketConbinationMask(bet1: CombinableBet, bet2: CombinableBet) {
    return (
        bet1.tournamentAllowMarketCombinations &&
        bet2.tournamentAllowMarketCombinations &&
        bet1.marketGroupCombinationMask != null &&
        bet2.marketGroupCombinationMask != null
    );
}
